import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { RedemptionTrend } from '../../../services/analyticsService';

interface RedemptionTrendChartProps {
  data: RedemptionTrend[];
  title: string;
}

const RedemptionTrendChart: React.FC<RedemptionTrendChartProps> = ({ data, title }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-AT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-AT', {
      month: 'short',
      day: 'numeric'
    });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-4 shadow-lg">
          <p className="text-foreground font-medium mb-2">
            {new Date(label).toLocaleDateString('de-AT', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between space-x-4 mb-1">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: entry.color }}
                ></div>
                <span className="text-sm text-muted-foreground">{entry.name}:</span>
              </div>
              <span className="text-sm font-medium text-foreground">
                {entry.dataKey === 'redemptions' || entry.dataKey === 'offers' ? entry.value.toLocaleString('de-AT')
                  : formatCurrency(entry.value)
                }
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const totalRedemptions = data.reduce((sum, item) => sum + item.redemptions, 0);
  const totalRevenue = data.reduce((sum, item) => sum + item.revenue, 0);
  const averageDaily = totalRedemptions / Math.max(data.length, 1);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Dual-axis view of redemption volume and revenue correlation
          </p>
        </div>
        
        <div className="flex items-center space-x-6 mt-4 sm:mt-0">
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Redemptions</p>
            <p className="text-lg font-semibold text-foreground">{totalRedemptions.toLocaleString('de-AT')}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Revenue</p>
            <p className="text-lg font-semibold text-foreground">{formatCurrency(totalRevenue)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Daily Avg</p>
            <p className="text-lg font-semibold text-foreground">{averageDaily.toFixed(1)}</p>
          </div>
        </div>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              className="text-xs"
              stroke="currentColor"
            />
            <YAxis 
              yAxisId="left"
              className="text-xs"
              stroke="currentColor"
            />
            <YAxis 
              yAxisId="right" 
              orientation="right"
              tickFormatter={formatCurrency}
              className="text-xs"
              stroke="currentColor"
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="redemptions"
              stroke="#178EEF"
              strokeWidth={2}
              dot={{ fill: '#178EEF', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#178EEF', strokeWidth: 2 }}
              name="Redemptions"
            />
            
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="revenue"
              stroke="#10B981"
              strokeWidth={2}
              dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2 }}
              name="Revenue (€)"
            />
            
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="savings"
              stroke="#F59E0B"
              strokeWidth={2}
              dot={{ fill: '#F59E0B', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#F59E0B', strokeWidth: 2 }}
              name="Savings (€)"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 pt-4 border-t border-border">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
          <div>
            <div className="flex items-center justify-center space-x-2 mb-1">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-sm text-muted-foreground">Redemption Volume</span>
            </div>
            <p className="text-xs text-muted-foreground">Daily transaction count</p>
          </div>
          <div>
            <div className="flex items-center justify-center space-x-2 mb-1">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm text-muted-foreground">Revenue Generated</span>
            </div>
            <p className="text-xs text-muted-foreground">Final transaction value</p>
          </div>
          <div>
            <div className="flex items-center justify-center space-x-2 mb-1">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              <span className="text-sm text-muted-foreground">User Savings</span>
            </div>
            <p className="text-xs text-muted-foreground">Total discount amount</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedemptionTrendChart;